import apiService from "@/services/api";

export type MfaMethod = "TOTP" | "EMAIL";

export interface MfaStatus {
	enabled: boolean;
	method: MfaMethod | null;
	backupCodesRemaining: number;
}

export interface MfaSetupTotpResponse {
	method: "TOTP";
	secret: string;
	qrCodeDataUrl: string;
}

export interface MfaSetupEmailResponse {
	method: "EMAIL";
}

export type MfaSetupResponse = MfaSetupTotpResponse | MfaSetupEmailResponse;

export interface MfaActivateResponse {
	backupCodes: string[];
}

export interface TrustedDevice {
	id: string;
	label: string;
	lastUsedAt: string | null;
	createdAt: string;
	expiresAt: string;
}

/**
 * Verifica o código MFA durante o fluxo de login (sem Bearer token).
 * Retorna a resposta normal de login (+ trustedDeviceToken quando rememberDevice).
 */
export async function verifyMfaCode(payload: {
	mfaChallengeToken: string;
	code: string;
	rememberDevice?: boolean;
}) {
	return apiService.post("/auth/mfa/verify", payload);
}

/**
 * Reenvia o código MFA por e-mail (apenas método EMAIL).
 */
export async function resendMfaCode(mfaChallengeToken: string) {
	return apiService.post("/auth/mfa/resend", { mfaChallengeToken });
}

/**
 * Status do MFA da conta autenticada.
 */
export async function getMfaStatus(): Promise<MfaStatus> {
	return apiService.get("/auth/mfa/status");
}

/**
 * Inicia a configuração do MFA. TOTP devolve secret + QR Code; EMAIL dispara código.
 */
export async function setupMfa(method: MfaMethod): Promise<MfaSetupResponse> {
	return apiService.post("/auth/mfa/setup", { method });
}

/**
 * Ativa o MFA confirmando o código. Devolve os códigos de backup (mostrar UMA vez).
 */
export async function activateMfa(code: string): Promise<MfaActivateResponse> {
	return apiService.post("/auth/mfa/activate", { code });
}

/**
 * Desativa o MFA (exige código de verificação).
 */
export async function disableMfa(code: string) {
	return apiService.post("/auth/mfa/disable", { code });
}

/**
 * Regenera os códigos de backup (invalida os anteriores). Exige um código MFA atual.
 */
export async function regenerateBackupCodes(
	code: string,
): Promise<MfaActivateResponse> {
	return apiService.post("/auth/mfa/backup-codes/regenerate", { code });
}

/**
 * Lista os dispositivos confiáveis da conta.
 */
export async function getTrustedDevices(): Promise<TrustedDevice[]> {
	return apiService.get("/auth/mfa/trusted-devices");
}

/**
 * Revoga um dispositivo confiável.
 */
export async function revokeTrustedDevice(id: string) {
	return apiService.delete(`/auth/mfa/trusted-devices/${id}`);
}
